import {
    api,
    apiErrorMessage,
    listSpec,
    oneSpec,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    DetailRow,
    FieldGrid,
    Input,
    ObjectRef,
    PageHeader,
    PublicId,
    ReasonDialog,
    SearchableSelect,
    StatusBadge,
    statusLabel,
    StoreName,
    Textarea,
    UnsavedGuard,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import type { CaseNote, CaseRecord, CaseUpdateRequest } from '@/api/types';
import { ActivityCard } from '@/components/activity';
import { Comments } from '@/components/comments';
import { DetailBody } from '@/components/detail';
import { detailTitle } from '@/components/detail-title';
import { EntityTags } from '@/components/entity-tags';
import { optionsOf } from '@/lib/options';
import { P } from '@/lib/permissions';
import { useList, useOne, useWrite } from '@/lib/queries';
import { useMe, who } from '@/lib/staff';
import { ADMIN_STATUSES } from '@/lib/statuses';
import { PRIORITIES } from '@/pages/cases/index';
import { subjectPath } from '@/pages/queues/index';

/** The reads of `/cases/:id` (BM-46 §2.0; the comments page by cursor and load in the browser). */
export const caseReads = (id: string | undefined) => ({
    record: oneSpec(`/staff/cases/${id ?? ''}`),
    notes: listSpec(`/staff/cases/${id ?? ''}/notes`, { limit: 100 }),
});

export function CasePage() {
    const { id = '' } = useParams();
    const me = useMe();
    const can = useAuthStore((s) => s.can);
    const reads = caseReads(id);
    const query = useOne<CaseRecord>(reads.record.path);
    const notes = useList<CaseNote>(reads.notes.path, reads.notes.params);
    const [error, setError] = useState<string | null>(null);
    const [closing, setClosing] = useState(false);
    const [note, setNote] = useState('');
    const [title, setTitle] = useState<string | null>(null);
    const update = useWrite(
        async (body: CaseUpdateRequest) =>
            (await api().patch<CaseRecord>(`/staff/cases/${id}`, body)).data,
        ['/staff/cases', '/staff/queues'],
    );
    const addNote = useWrite(
        async (body: string) =>
            (await api().post<CaseNote>(`/staff/cases/${id}/notes`, { body }))
                .data,
        [`/staff/cases/${id}/notes`, '/staff/cases'],
    );
    const onError = (e: unknown) => setError(apiErrorMessage(e));
    const patch = (c: CaseRecord, body: Omit<CaseUpdateRequest, 'version'>) =>
        update.mutate({ version: c.version, ...body }, { onError });
    return (
        <div>
            {/* BM-46 §2.9: leaving with unsaved changes asks first */}
            <UnsavedGuard dirty={note.trim() !== '' || title !== null} />
            <PageHeader
                crumbs={[{ label: 'Cases', href: '/cases' }]}
                {...detailTitle(query, query.data?.data.title, id)}
                meta={
                    <>
                        <PublicId id={id} short={false} />
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </>
                }
            />
            <DetailBody query={query} title="Case">
                {(c) => (
                    <div className="space-y-4">
                        {error ? (
                            <p
                                role="alert"
                                className="text-destructive text-sm"
                            >
                                {error}
                            </p>
                        ) : null}
                        <Card>
                            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                                <CardTitle className="text-base">
                                    {title === null ? (
                                        c.title
                                    ) : (
                                        <Input
                                            aria-label="Title"
                                            value={title}
                                            onChange={(e) =>
                                                setTitle(e.target.value)
                                            }
                                            onBlur={() => {
                                                if (title && title !== c.title)
                                                    patch(c, { title });
                                                setTitle(null);
                                            }}
                                            autoFocus
                                        />
                                    )}
                                </CardTitle>
                                {can(P.casesWrite) && c.state !== 'closed' ? (
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => setTitle(c.title)}
                                        >
                                            Rename
                                        </Button>
                                        {c.state === 'open' ? (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() =>
                                                    patch(c, {
                                                        state: 'waiting',
                                                    })
                                                }
                                            >
                                                Mark waiting
                                            </Button>
                                        ) : (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() =>
                                                    patch(c, { state: 'open' })
                                                }
                                            >
                                                Reopen
                                            </Button>
                                        )}
                                        <Button
                                            size="sm"
                                            variant="destructive"
                                            onClick={() => setClosing(true)}
                                        >
                                            Close case
                                        </Button>
                                    </div>
                                ) : null}
                            </CardHeader>
                            <CardContent>
                                <FieldGrid>
                                    <DetailRow label="State" layout="stacked">
                                        <StatusBadge
                                            status={c.state}
                                            domain="case"
                                        />
                                        {c.closed_reason ? (
                                            <span className="text-muted-foreground ml-2 text-xs">
                                                {c.closed_reason}
                                            </span>
                                        ) : null}
                                    </DetailRow>
                                    <DetailRow
                                        label="Kind"
                                        value={statusLabel(c.kind)}
                                        layout="stacked"
                                    />
                                    <DetailRow
                                        label="Priority"
                                        layout="stacked"
                                    >
                                        {can(P.casesWrite) &&
                                        c.state !== 'closed' ? (
                                            <SearchableSelect
                                                label="Priority"
                                                options={optionsOf(PRIORITIES)}
                                                value={c.priority}
                                                clearable={false}
                                                onChange={(v) =>
                                                    v
                                                        ? patch(c, {
                                                              priority:
                                                                  v as CaseRecord['priority'],
                                                          })
                                                        : undefined
                                                }
                                            />
                                        ) : (
                                            <StatusBadge
                                                status={c.priority}
                                                domain={ADMIN_STATUSES.priority}
                                            />
                                        )}
                                    </DetailRow>
                                    <DetailRow
                                        label="Assigned to"
                                        layout="stacked"
                                    >
                                        {who(c.assigned_to, me)}
                                        {can(P.casesAssign) &&
                                        c.state !== 'closed' ? (
                                            <Button
                                                size="sm"
                                                variant="ghost"
                                                className="ml-2"
                                                onClick={() =>
                                                    patch(c, {
                                                        assigned_to:
                                                            c.assigned_to === me
                                                                ? null
                                                                : me,
                                                    })
                                                }
                                            >
                                                {c.assigned_to === me
                                                    ? 'Release'
                                                    : 'Take'}
                                            </Button>
                                        ) : null}
                                    </DetailRow>
                                    <DetailRow
                                        label="Merchant"
                                        layout="stacked"
                                    >
                                        {c.merchant_id ? (
                                            <ObjectRef id={c.merchant_id} />
                                        ) : (
                                            '—'
                                        )}
                                    </DetailRow>
                                    {/* BM-87: the store of the charge the case came from; nothing on a case of no store */}
                                    {c.store ? (
                                        <DetailRow
                                            label="Store"
                                            layout="stacked"
                                        >
                                            <StoreName store={c.store} />
                                        </DetailRow>
                                    ) : null}
                                    <DetailRow label="Opened" layout="stacked">
                                        <DateTime
                                            zone="inline"
                                            value={c.created_at}
                                            empty="—"
                                        />{' '}
                                        by {who(c.opened_by, me)}
                                    </DetailRow>
                                    {c.closed_at ? (
                                        <DetailRow
                                            label="Closed"
                                            layout="stacked"
                                        >
                                            <DateTime
                                                zone="inline"
                                                value={c.closed_at}
                                                empty="—"
                                            />{' '}
                                            by {who(c.closed_by, me)}
                                        </DetailRow>
                                    ) : null}
                                    <DetailRow label="Tags" layout="stacked">
                                        <EntityTags
                                            entity={{ type: 'case', id: c.id }}
                                            tags={c.tags ?? []}
                                            canEdit={can(P.casesWrite)}
                                            canCreate={can(P.casesWrite)}
                                        />
                                    </DetailRow>
                                </FieldGrid>
                                {c.links.length > 0 ? (
                                    <div className="mt-4 text-sm">
                                        <p className="text-muted-foreground mb-1 text-xs">
                                            Linked
                                        </p>
                                        <ul className="flex flex-wrap gap-2">
                                            {c.links.map((l) => {
                                                const to = subjectPath(
                                                    l.type,
                                                    l.id,
                                                );
                                                return (
                                                    <li
                                                        key={`${l.type}:${l.id}`}
                                                        className="font-mono text-xs"
                                                    >
                                                        {to ? (
                                                            <Link
                                                                to={to}
                                                                className="underline"
                                                            >
                                                                {l.type} {l.id}
                                                            </Link>
                                                        ) : (
                                                            `${l.type} ${l.id}`
                                                        )}
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                ) : null}
                                {c.kind === 'offboarding' ? (
                                    <div
                                        className="mt-4 text-sm"
                                        data-testid="offboarding-progress"
                                    >
                                        <p className="text-muted-foreground mb-1 text-xs">
                                            Erasure progress (service → erased
                                            at)
                                        </p>
                                        <ul className="grid grid-cols-1 gap-1 md:grid-cols-2">
                                            {Object.entries(
                                                c.progress as Record<
                                                    string,
                                                    string
                                                >,
                                            ).map(([service, at]) => (
                                                <li
                                                    key={service}
                                                    className="flex justify-between gap-2"
                                                >
                                                    <span>{service}</span>
                                                    <DateTime
                                                        value={at}
                                                        empty="—"
                                                    />
                                                </li>
                                            ))}
                                            {Object.keys(c.progress).length ===
                                            0 ? (
                                                <li className="text-muted-foreground">
                                                    No service has reported yet.
                                                </li>
                                            ) : null}
                                        </ul>
                                    </div>
                                ) : null}
                            </CardContent>
                        </Card>
                        <Card>
                            <CardHeader>
                                <CardTitle className="text-sm font-medium">
                                    Notes ({c.notes_count})
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <ul
                                    className="divide-y text-sm"
                                    data-testid="case-notes"
                                >
                                    {(notes.data?.data.items ?? []).map((n) => (
                                        <li key={n.id} className="py-2">
                                            <p className="whitespace-pre-wrap">
                                                {n.body}
                                            </p>
                                            <p className="text-muted-foreground text-xs">
                                                {who(n.author_id, me)} ·{' '}
                                                <DateTime
                                                    value={n.created_at}
                                                    empty="—"
                                                />
                                            </p>
                                        </li>
                                    ))}
                                    {notes.data &&
                                    notes.data.data.items.length === 0 ? (
                                        <li className="text-muted-foreground py-2">
                                            No notes yet.
                                        </li>
                                    ) : null}
                                </ul>
                                {can(P.casesWrite) && c.state !== 'closed' ? (
                                    <form
                                        method="post"
                                        className="space-y-2"
                                        data-testid="note-form"
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            if (note.trim().length === 0)
                                                return;
                                            setError(null);
                                            addNote.mutate(note.trim(), {
                                                onSuccess: () => setNote(''),
                                                onError,
                                            });
                                        }}
                                    >
                                        <Textarea
                                            aria-label="Note"
                                            rows={3}
                                            value={note}
                                            onChange={(e) =>
                                                setNote(e.target.value)
                                            }
                                            placeholder="What you found, what you did…"
                                        />
                                        <Button
                                            type="submit"
                                            size="sm"
                                            disabled={addNote.isPending}
                                        >
                                            Add note
                                        </Button>
                                    </form>
                                ) : null}
                            </CardContent>
                        </Card>
                        <ReasonDialog
                            open={closing}
                            onOpenChange={setClosing}
                            title="Close this case"
                            description="The reason is kept on the case and published with case.closed."
                            confirmLabel="Close case"
                            destructive
                            busy={update.isPending}
                            error={update.error}
                            onConfirm={(reason) =>
                                update.mutate(
                                    {
                                        version: c.version,
                                        state: 'closed',
                                        closed_reason: reason,
                                    },
                                    { onSuccess: () => setClosing(false) },
                                )
                            }
                        />
                    </div>
                )}
            </DetailBody>
            <div className="mt-4">
                <Comments subjectType="case" subjectId={id} />
            </div>
            <div className="mt-4">
                <ActivityCard entity="case" id={id} />
            </div>
        </div>
    );
}
